import React from 'react';
import { TabContextProps } from '@sharefiledev/flow-web';
import { ProjectTilesExtensionParams } from '@sharefiledev/projects-pilet';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import { Skeleton, Spin } from 'antd';
import { StorageFileTableParams } from '../../sdk';
import { PreviewFileViewerWrapperProps } from '../preview/PreviewFileViewerWrapper';
import { CountProps } from './Count';
import { DownloadWrapperProps } from './Download';
import { EditDesktopWrapperProps } from './EditDesktop';
import { EditOnlineWrapperProps } from './EditOnline';
import { LinkToContainersBtnWrapperParams } from './LinkToContainers';
import { MoveCopyBtnWrapperProps } from './MoveCopyWrapper';
import { NoteBtnWrapperProps } from './NoteWrapper';
import { ShareModalWrapperProps } from './ShareModal';
import { WorkflowsStorageZoneDropDownParams } from './WorkflowsStorageZoneDropDown';

export type ExtensionImplementationProps<TParams> = TParams & { piletApi: PiletApi };

export type FileHomeTileProps = ExtensionImplementationProps<ProjectTilesExtensionParams>;

type TabUIProps = {
	projectUri: string;
	tabContext: TabContextProps;
};

export type TabProps = ExtensionImplementationProps<TabUIProps>;

const LazyFileHomeTile = React.lazy(() => import('./FilesHomeTile'));
export const AsyncFileHomeTileExtensionSlot: React.FC<FileHomeTileProps> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyFileHomeTile {...props} />
		</React.Suspense>
	);
};

const LazyFilesTable = React.lazy(() => import('./FilesTable'));
export const AsyncLazyFilesTable: React.FC<
	ExtensionImplementationProps<StorageFileTableParams>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyFilesTable {...props} />
		</React.Suspense>
	);
};

const LazyCount = React.lazy(() => import('./Count'));
export const AsyncLazyCount: React.FC<CountProps> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyCount {...props} />
		</React.Suspense>
	);
};

const LazyDownload = React.lazy(() => import('./Download'));
export const AsyncLazyDownload: React.FC<DownloadWrapperProps> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyDownload {...props} />
		</React.Suspense>
	);
};

const LazyMoveCopy = React.lazy(() => import('./MoveCopyWrapper'));
export const AsyncLazyMoveCopy: React.FC<MoveCopyBtnWrapperProps> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyMoveCopy {...props} />
		</React.Suspense>
	);
};

const LazyEditOnline = React.lazy(() => import('./EditOnline'));
export const AsyncLazyEditOnline: React.FC<EditOnlineWrapperProps> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyEditOnline {...props} />
		</React.Suspense>
	);
};

const LazyEditDesktop = React.lazy(() => import('./EditDesktop'));
export const AsyncLazyEditDesktop: React.FC<EditDesktopWrapperProps> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyEditDesktop {...props} />
		</React.Suspense>
	);
};

const LazyShareModal = React.lazy(() => import('./ShareModal'));
export const AsyncLazyShareModal: React.FC<ShareModalWrapperProps> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyShareModal {...props} />
		</React.Suspense>
	);
};

const LazyProjectsTab = React.lazy(() => import('./ProjectsTab'));
export const AsyncLazyProjectsTab: React.FC<TabProps> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyProjectsTab {...props} />
		</React.Suspense>
	);
};

const LazyRecentlyDeletedTab = React.lazy(() => import('./RecentlyDeletedTab'));
export const AsyncLazyRecentlyDeletedTab: React.FC<TabProps> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyRecentlyDeletedTab {...props} />
		</React.Suspense>
	);
};

const LazyPreviewFileViewerWrapperExtension = React.lazy(() =>
	import('../preview').then(module => ({
		default: module['PreviewFileViewerWrapperExtension'],
	}))
);
export const AsyncLazyPreviewFileViewerWrapperExtension: React.FC<
	PreviewFileViewerWrapperProps
> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyPreviewFileViewerWrapperExtension {...props} />
		</React.Suspense>
	);
};

const LazyLinkToContainers = React.lazy(() => import('./LinkToContainers'));
export const AsyncLazyLinkToContainers: React.FC<
	LinkToContainersBtnWrapperParams
> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyLinkToContainers {...props} />
		</React.Suspense>
	);
};

const LazyFileViewerDrawer = React.lazy(() =>
	import('../preview').then(module => ({
		default: module['PreviewFileViewerWrapper'],
	}))
);
export const AsyncLazyFileViewerDrawer: React.FC<
	PreviewFileViewerWrapperProps
> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyFileViewerDrawer {...props} />
		</React.Suspense>
	);
};

const LazyStorageZoneDropDown = React.lazy(() => import('./StorageZoneDropDown'));
export const AsyncLazyStorageZoneDropDown: React.FC<any> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyStorageZoneDropDown {...props} />
		</React.Suspense>
	);
};

const LazyWorkflowsStorageZoneDropDown = React.lazy(
	() => import('./WorkflowsStorageZoneDropDown')
);
export const AsyncLazyWorkflowsStorageZoneDropDown: React.FC<
	WorkflowsStorageZoneDropDownParams
> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyWorkflowsStorageZoneDropDown {...props} />
		</React.Suspense>
	);
};

const LazyNote = React.lazy(() => import('./NoteWrapper'));
export const AsyncLazyNote: React.FC<NoteBtnWrapperProps> = props => {
	return (
		<React.Suspense fallback={<Spin />}>
			<LazyNote {...props} />
		</React.Suspense>
	);
};
